import React, { ReactNode } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useUser } from '../../contexts/UserContext';
import { useBotState } from '../../contexts/BotStateContext';
import MaintenancePage from '../../pages/MaintenancePage';
import MaintenanceBanner from '../maintenance/MaintenanceBanner';
import { colors } from '../../theme/themeConfig';
import { isTelegramWebApp } from '../../services/telegram';

interface AuthGuardProps {
  children: ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { user, telegramUser, isLoading: isUserLoading, error: userError } = useUser();
  const { isActive, maintenanceMessage, isLoading: isBotStateLoading } = useBotState();
  
  // Show loading state while initializing
  if (isUserLoading || isBotStateLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: colors.background.default,
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  // Check if running inside Telegram WebApp
  if (!isTelegramWebApp() && process.env.NODE_ENV === 'production') {
    return <Box sx={{ p: 3 }}>Это приложение должно быть запущено в Telegram</Box>;
  }

  // If telegram user data is not available, show maintenance page
  if (!telegramUser) {
    return <MaintenancePage message="Не удалось получить данные пользователя Telegram. Пожалуйста, попробуйте позже." />;
  }

  // Show maintenance page if bot is inactive and user is not admin
  if (!isActive && user?.isAdmin !== true) {
    return <MaintenancePage message={maintenanceMessage} />;
  }

  // Admin user with bot in maintenance mode
  const showMaintenanceBanner = !isActive && user?.isAdmin === true;

  return (
    <>
      {showMaintenanceBanner && (
        <MaintenanceBanner message={maintenanceMessage} />
      )}
      {children}
    </>
  );
};

export default AuthGuard;
